import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SearchForm, ModalInfo, TooltipItem, EventDashboardDetails, AlertNotice,
} from 'shared/components';
import PlusIcon from 'assets/svgs/plusIcon.svg';
import { isEmpty } from 'lodash';
import './AssignNonPersonnelStaff.scss';
import PropTypes from 'prop-types';
import {
  AddNonPersonnelStaff,
} from 'features/staff-and-rooms';

const searchStaff = (staffList, value) => staffList.filter((staff) => {
  const fullName = staff.lastName.trim().concat(', ', staff.firstName.trim());
  const emailAddress = staff.emailAddress.trim();
  const newValue = value.toLowerCase();

  return (fullName.toLowerCase()).indexOf(newValue) !== -1
    || (emailAddress.toLowerCase()).indexOf(newValue) !== -1;
});

const isCurrentUser = (currentAssignedStaff, staff) => currentAssignedStaff
  && currentAssignedStaff.nonPersonnelId === staff.nonPersonnelId;

const AssignNonPersonnelStaff = (props) => {
  const { t } = useTranslation();
  const {
    modalOpen,
    tcEvent,
    currentAssignedStaff,
    currentRole,
    nonPersonnelStaffList,
    toggleModal,
    handleAssign,
    handleUnAssign,
    handleAddNonPersonnel,
    testCenterCycle,
    error,
    loading,
  } = props;
  const [searchValue, setSearchValue] = useState('');
  const [staffList, setStaffList] = useState(nonPersonnelStaffList);
  const [isNewUser, setIsNewUser] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showAddNonPersonnelError, setShowAddNonPersonnelError] = useState(false);
  const testingEventName = tcEvent.testingEvent.name;

  useEffect(() => {
    setStaffList(nonPersonnelStaffList);
  }, [nonPersonnelStaffList]);

  const resetError = () => {
    setIsNewUser(false);
    setShowError(false);
    setShowAddNonPersonnelError(false);
  };

  useEffect(() => {
    if (loading && isNewUser) setIsNewUser(true);
    if (loading === false && isNewUser && error) {
      setIsNewUser(true);
      setShowAddNonPersonnelError(true);
      setShowError(false);
    }
    if (loading === false && isNewUser && !error) {
      resetError();
    }
    if (loading === false && !isNewUser && error) {
      setShowError(true);
      setShowAddNonPersonnelError(false);
    }
  }, [loading, error]);

  const onSearchChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    const searchStaffList = value === '' ? searchStaff(nonPersonnelStaffList, value) : staffList;
    setStaffList(searchStaffList);
  };

  const onSearchStaff = () => {
    if (searchValue) {
      setStaffList(searchStaff(nonPersonnelStaffList, searchValue));
    }
  };

  const clearSearch = () => {
    setSearchValue('');
    setStaffList(nonPersonnelStaffList);
  };

  const renderTitle = () => {
    if (isNewUser) {
      return t('staffAndRoomsManagement.assignStaff.addTitle');
    }
    if (currentAssignedStaff && !isEmpty(currentAssignedStaff)) {
      return t('staffAndRoomsManagement.assignStaff.editTitle');
    }
    return t('staffAndRoomsManagement.assignStaff.assignTitle');
  };

  const handleAddNonPersonnelStaff = (nonPersonnel) => {
    handleAddNonPersonnel(nonPersonnel);
  };

  const handleModalClose = () => {
    resetError();
    clearSearch();
    toggleModal();
  };

  const handleAddNonPersonnelBack = () => {
    resetError();
  };

  return (
    <div>
      <ModalInfo
        modalIsOpen={modalOpen}
        closeModal={handleModalClose}
        headerTitle={renderTitle()}
        onSubmit={() => {
        }}
        className="modal-assign-non-personnel-staff"
        containerClass="modal-assign-non-personnel-staff"
        hideButtons
      >
        <section className="assign-staff">
          <div className="top-section">
            <h4>{currentRole && currentRole.name}</h4>
            <EventDashboardDetails
              date={testingEventName}
              name={testCenterCycle.testCenterProgram.testCenter
              && testCenterCycle.testCenterProgram.testCenter.name}
            />
          </div>
          {!isNewUser
            ? (
              <>
                <p className="search-instructions">
                  {t('staffAndRoomsManagement.assignStaff.searchNonPersonnelStaffInformationMessage', { name: currentRole && currentRole.name })}
                </p>
                <p className="search-instructions">
                  <span className="fw-bold">{ t('staffAndRoomsManagement.assignStaff.searchNonPersonnelStaffInformationMessageNoteLabel') }</span>
                  { t('staffAndRoomsManagement.assignStaff.searchNonPersonnelStaffInformationNoteMessage') }
                </p>
                <p className="assigned-non-personnel">
                  <span className="fw-bold">
                    {currentRole && currentRole.name}
                    : &nbsp;
                    {
                      !isEmpty(currentAssignedStaff)
                        ? `${currentAssignedStaff.lastName}, ${currentAssignedStaff.firstName}`
                        : t('staffManagement.unassignedLabel')
                        }
                  </span>
                </p>
                <div className="staff-search">
                  <SearchForm
                    searchValue={searchValue}
                    onChange={onSearchChange}
                    onSearch={onSearchStaff}
                    placeholder={t('staffAndRoomsManagement.assignStaff.searchPlaceholder')}
                    displayIconButton={false}
                    clearSearch={clearSearch}
                  />
                  <button
                    type="button"
                    className="btn btn-outline btn-small add-user-button"
                    onClick={
                      () => setIsNewUser(true)
                    }
                  >
                    <PlusIcon />
                    {t('staffAndRoomsManagement.assignStaff.addStaff')}
                  </button>
                </div>
                {
                  showError
                  && <AlertNotice message={t('errors.serverError')} inline type="error" />
                 }
                <div className="staff-assign-list">
                  <div className="staff-assign-header hide-mobile">
                    <div>{currentRole && currentRole.name}</div>
                    <div>{t('staffAndRoomsManagement.assignStaff.emailAddress')}</div>
                    <div>{t('staffAndRoomsManagement.assignStaff.phone')}</div>
                    <div>{t('staffAndRoomsManagement.assignStaff.action')}</div>
                  </div>
                  {staffList.map((staff) => (
                    <div className="staff-assign-row" key={`staff-item-${staff.nonPersonnelId}`}>
                      <TooltipItem item={`${staff.lastName}, ${staff.firstName}`} customClass="fw-bold" />
                      <TooltipItem item={`${staff.emailAddress}`} />
                      <TooltipItem item={`${staff.phone}`} />
                      <div>
                        {
                          (isCurrentUser(currentAssignedStaff, staff))
                            ? (
                              <button
                                type="button"
                                className="btn btn-outline btn-small"
                                onClick={
                                  () => handleUnAssign(tcEvent.id, staff.nonPersonnelId,
                                    currentRole && currentRole.role)
                                }
                              >
                                <PlusIcon />
                                {t('common.labels.unAssign')}
                              </button>
                            )
                            : (
                              <button
                                type="button"
                                className="btn btn-outline btn-small"
                                onClick={
                                  () => handleAssign(tcEvent.id, staff.nonPersonnelId,
                                    currentRole && currentRole.role)
                                }
                              >
                                <PlusIcon />
                                {t('common.labels.assign')}
                              </button>
                            )
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <AddNonPersonnelStaff
                handleSubmit={handleAddNonPersonnelStaff}
                handleCancel={handleAddNonPersonnelBack}
                showError={showAddNonPersonnelError}
              />
            )}
        </section>
      </ModalInfo>
    </div>
  );
};

export default AssignNonPersonnelStaff;

AssignNonPersonnelStaff.propTypes = {
  nonPersonnelStaffList: PropTypes.arrayOf(PropTypes.shape({})),
  modalOpen: PropTypes.bool.isRequired,
  tcEvent: PropTypes.shape({
    id: PropTypes.number,
    testingEvent: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
  testCenterCycle: PropTypes.shape({
    testCenterProgram: PropTypes.shape({
      testCenter: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  }).isRequired,
  currentAssignedStaff: PropTypes.shape({
    lastName: PropTypes.string,
    firstName: PropTypes.string,
  }),
  currentRole: PropTypes.shape({
    name: PropTypes.string,
    role: PropTypes.string,
  }),
  toggleModal: PropTypes.func.isRequired,
  handleAssign: PropTypes.func.isRequired,
  handleUnAssign: PropTypes.func.isRequired,
  handleAddNonPersonnel: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

AssignNonPersonnelStaff.defaultProps = {
  nonPersonnelStaffList: [],
  currentAssignedStaff: {},
  currentRole: {},
  loading: false,
  error: '',
};
